import BaseModel from "./BaseModel";
import md5 from "md5";

export enum SkillStatus {
  NotStarted = "NotStarted",
  InProgress = "InProgress",
  Completed = "Completed",
}

export class SkillModel {
  private __name: string;
  private __identifier: string;

  private __status: SkillStatus = SkillStatus.NotStarted;
  private __parent: SkillModel | null = null;
  private __subSkills?: SkillModel[];

  constructor(name: string, identifier: string, subSkills?: SkillModel[]) {
    this.__name = name;
    this.__identifier = identifier;
    this.__subSkills = subSkills;
    this.__subSkills?.forEach((skill) => (skill.__parent = this));
  }

  get name(): string {
    return this.__name;
  }

  get status(): SkillStatus {
    if (this.isLeaf) {
      return this.__status;
    }
    const subSkillsStatus = this.__subSkills?.map((skill) => skill.status);
    if (subSkillsStatus?.every((status) => status === SkillStatus.Completed)) {
      this.__status = SkillStatus.Completed;
    } else if (
      subSkillsStatus?.some((status) => status !== SkillStatus.NotStarted)
    ) {
      this.__status = SkillStatus.InProgress;
    } else this.__status = SkillStatus.NotStarted;
    return this.__status;
  }

  setStatus(status: SkillStatus) {
    if (this.isLeaf) {
      this.__status = status;
      return;
    }
    this.__subSkills?.forEach((skill) => skill.setStatus(status));
  }

  setCompleted() {
    this.setStatus(SkillStatus.Completed);
  }

  setNotStarted() {
    this.setStatus(SkillStatus.NotStarted);
  }

  get isCompleted(): boolean {
    return this.status === SkillStatus.Completed;
  }

  get isInProgress(): boolean {
    return this.status === SkillStatus.InProgress;
  }

  get isNotStarted(): boolean {
    return this.status === SkillStatus.NotStarted;
  }

  get subSkills() {
    return this.__subSkills || [];
  }

  get parent() {
    return this.__parent;
  }

  get isLeaf() {
    return (
      this.__subSkills === undefined ||
      this.__subSkills?.length === 0 ||
      this.__subSkills === null
    );
  }

  get hashedPath() {
    const path = [];
    let currentSkill: SkillModel | null = this;

    while (currentSkill) {
      path.push(currentSkill.__identifier);
      currentSkill = currentSkill.parent;
    }

    const hashedPath = md5(path.reverse().join(">"));

    return hashedPath;
  }

  get leafSkills(): SkillModel[] {
    if (this.isLeaf) {
      return [this];
    }
    return this.subSkills.flatMap((skill) => skill.leafSkills);
  }

  get completedSubSkillsCount() {
    return this.subSkills.filter((skill) => skill.isCompleted).length;
  }
}
